import type { Frame } from "../renderer/Frame";
import type { DrawingObject } from "./types";
import { isPositionTool } from "./types";
import { distToSegment, inPolygon, inRect, screenPts, textBox, XY } from "./geometry";
import { outline, CLOSED, POINT_MARKERS } from "./shapes";
import { volumeProfile } from "./analytics";

export interface Hit { id: string; handle: number } // handle = point index, -1 = body

const HANDLE_R = 7;
const LINE_TOL = 5;

// Screen points of a drawing; anchored text is pinned to a plot-relative position instead of time/price.
export function drawingPts(f: Frame, d: DrawingObject): XY[] {
  if (d.type === "anchoredtext" && d.meta && d.meta.ax != null) return [{ x: f.L.plotLeft + Number(d.meta.ax) * f.L.plotW, y: f.L.plotTop + Number(d.meta.ay) * f.L.priceH }];
  return screenPts(f, d);
}

// Top-most drawing under the cursor. Handles win over bodies; later objects win over earlier ones.
export function hitTest(f: Frame, list: DrawingObject[], x: number, y: number): Hit | null {
  const p = { x, y };
  for (let i = list.length - 1; i >= 0; i--) {
    const d = list[i];
    if (!d.visible) continue;
    const pts = drawingPts(f, d);
    if (d.type !== "brush" && d.type !== "highlighter") {
      for (let h = 0; h < pts.length; h++) {
        const hp = handlePos(f, d, pts, h);
        if (Math.hypot(hp.x - p.x, hp.y - p.y) <= HANDLE_R) return { id: d.id, handle: d.locked ? -1 : h };
      }
    }
    if (hitBody(f, d, pts, p)) return { id: d.id, handle: -1 };
  }
  return null;
}

export function handlePos(f: Frame, d: DrawingObject, pts: XY[], h: number): XY {
  if (d.type === "hline" || d.type === "hlbarrier" || d.type === "touchbarrier") return { x: f.L.plotLeft + f.L.plotW / 2, y: pts[0].y };
  if (d.type === "vline" || d.type === "expirymark") return { x: pts[0].x, y: f.L.plotTop + f.L.priceH / 2 };
  if (isPositionTool(d.type) && h === 2) return { x: pts[0].x, y: pts[2].y };
  return pts[h];
}

function nearLines(p: XY, lines: XY[][], tol: number): boolean {
  for (const q of lines) for (let i = 1; i < q.length; i++) if (distToSegment(p, q[i - 1], q[i]) <= tol) return true;
  return false;
}

function hitBody(f: Frame, d: DrawingObject, pts: XY[], p: XY): boolean {
  const [a, b, c] = pts;
  switch (d.type) {
    case "text": case "anchoredtext": {
      const tb = textBox(f.ctx, a, d.text || "", d.style.fontSize);
      return inRect(p, { x: tb.x, y: tb.y }, { x: tb.x + tb.w, y: tb.y + tb.h }, 2);
    }
    case "callout": case "pricenote": {
      if (!b) return false;
      const tb = textBox(f.ctx, b, d.text || "", d.style.fontSize);
      return inRect(p, { x: tb.x, y: tb.y }, { x: tb.x + tb.w, y: tb.y + tb.h + (d.type === "pricenote" ? 16 : 0) }, 2) || distToSegment(p, a, { x: tb.x, y: tb.y + tb.h / 2 }) <= LINE_TOL;
    }
    case "measure": return inRect(p, a, b, LINE_TOL);
    case "rr": case "longpos": case "shortpos": {
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
      const ys = [a.y, b.y, c?.y ?? a.y];
      return p.x >= x1 - LINE_TOL && p.x <= x2 + LINE_TOL && p.y >= Math.min(...ys) - LINE_TOL && p.y <= Math.max(...ys) + LINE_TOL;
    }
    case "fixedvp": case "anchoredvp": {
      const vp = volumeProfile(f, d.points[0], d.type === "fixedvp" ? d.points[1] ?? null : null);
      if (!vp) return false;
      return inRect(p, { x: f.x(vp.i1), y: f.y(vp.rows[vp.rows.length - 1].hi) }, { x: d.type === "fixedvp" ? f.x(vp.i2) : f.L.plotRight, y: f.y(vp.rows[0].lo) }, LINE_TOL);
    }
    case "brush": case "highlighter": return nearLines(p, [pts], Math.max(LINE_TOL, d.style.width / 2 + 2));
    default: {
      if (POINT_MARKERS.has(d.type) && Math.hypot(p.x - a.x, p.y - a.y - (d.type === "flag" || d.type === "signpost" || d.type === "note" ? -16 : 0)) <= 16) return true;
      const o = outline(f, d, pts);
      if (nearLines(p, o.lines, LINE_TOL) || (o.dashed && nearLines(p, o.dashed, LINE_TOL))) return true;
      if (CLOSED.has(d.type)) for (const q of o.fills) if (q.length >= 3 && inPolygon(p, q)) return true;
      for (const l of o.labels) if (Math.abs(p.y - l.y) <= 8 && Math.abs(p.x - l.x) <= 40) return true;
      return false;
    }
  }
}
